'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslations } from 'next-intl';
import { useMemo, useRef } from 'react';
import { SessionItem, type SessionPathname } from './SessionItem';

// Re-defining Session interface here or import from a shared types file
interface Session {
  sess_id: number;
  session_id: string;
  title: string;
  timestamp: number;
  isActive?: boolean;
  messagesFetched: boolean;
  access_role?: 'owner' | 'shared_direct' | 'shared_group';
  org_id?: string | null;
}

interface SessionListProps {
  groupedSessions: Record<string, Session[]>;
  groupOrder: string[];
  showContextBadge?: boolean;
  onSessionClick?: (() => void) | undefined;
  sessionPathname?: SessionPathname | undefined;
  /** Render per-group date headers. Defaults to true; false = flat list. */
  showGroupHeaders?: boolean | undefined;
}

interface FlattenedItem {
  type: 'group-header' | 'session';
  groupName?: string;
  session?: Session;
  index: number;
}

export const VirtualizedSessionList = ({
  groupedSessions,
  groupOrder,
  showContextBadge,
  onSessionClick,
  sessionPathname,
  showGroupHeaders = true,
}: SessionListProps) => {
  const t = useTranslations('Common');
  const parentRef = useRef<HTMLDivElement>(null);

  // Flatten the grouped sessions into a single array for virtualization
  const flattenedItems = useMemo(() => {
    const items: FlattenedItem[] = [];
    let globalIndex = 0;

    groupOrder.forEach((groupName) => {
      if (groupedSessions[groupName]) {
        // Add group header (skipped in flat "group by: none" mode)
        if (showGroupHeaders) {
          items.push({
            type: 'group-header',
            groupName,
            index: globalIndex++,
          });
        }

        // Add all sessions in the group
        groupedSessions[groupName].forEach((session) => {
          items.push({
            type: 'session',
            session,
            index: globalIndex++,
          });
        });
      }
    });

    return items;
  }, [groupedSessions, groupOrder, showGroupHeaders]);

  const hasSessions = flattenedItems.length > 0;

  // Setup virtualizer
  // Note: measureElement is disabled to prevent infinite loop when content causes layout shifts
  const virtualizer = useVirtualizer({
    count: flattenedItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const item = flattenedItems[index];
      if (!item || item.type === 'group-header') {
        return 40; // Height of group header
      }
      return 48; // Estimated height of session item
    },
    overscan: 5, // Render 5 items above/below viewport
  });

  if (!hasSessions) {
    return (
      <div className='flex-grow overflow-y-auto flex justify-center items-center h-[80vh] text-center text-content-muted'>
        <span className='text-sm'>{t('noChatHistoryFound')}</span>
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className='overflow-y-auto ml-2'
      style={{ height: '100%' }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        <ul className='space-y-0.5 pt-1 pb-1'>
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const item = flattenedItems[virtualItem.index];
            if (!item) return null;

            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {item.type === 'group-header' ? (
                  // Group header
                  <h4 className='sticky top-0 bg-surface-sidebar text-xs font-medium text-content-muted mt-1 mb-0 px-1.5 py-0.5 z-10'>
                    {item.groupName}
                  </h4>
                ) : item.session ? (
                  // Session item
                  <SessionItem
                    session={item.session}
                    showContextBadge={showContextBadge ?? false}
                    onSessionClick={onSessionClick}
                    sessionPathname={sessionPathname}
                  />
                ) : null}
              </div>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
